import {
	createAsyncThunk,
	createSlice,
	nanoid,
	type PayloadAction,
} from '@reduxjs/toolkit'
import * as Sentry from '@sentry/react'
import dayjs from 'dayjs'
import i18next from 'i18next'
import type { CompanyQuoteTemplate, CreateProjectsInput } from 'src/API'
import { defaultValuesForCompanyTable } from 'src/data/accountSettingsLabels'
import * as mutations from 'src/graphql/mutations'
import * as queries from 'src/graphql/queries'
import { clientApi } from 'src/redux/api/graphql/clientApi'
import { commentsApi } from 'src/redux/api/graphql/commentsApi'
import { companyApi } from 'src/redux/api/graphql/companyApi'
import { freelancersApi } from 'src/redux/api/graphql/freelancersApi'
import { quoteApi } from 'src/redux/api/graphql/quoteApi'
import { userInfoApi } from 'src/redux/api/graphql/userInfoApi'
import { quoteRestApi } from 'src/redux/api/rest/quoteRestApi'
import { subscriptionApi } from 'src/redux/api/rest/subscriptionApi'
import type { RootState, ThunkAppDispatch } from 'src/redux/store'
import type {
	ReduxClients,
	ReduxComments,
	ReduxCompany,
	ReduxCurrencyRate,
	ReduxExportInfo,
	ReduxFreelancers,
	ReduxUserInfo,
} from 'src/types/apiType'
import type {
	ApiStatus,
	CompanyLogoImagesType,
	CompanyNameType,
	FavouriteProject,
	UserInfo,
} from 'src/types/miscType'
import { API, graphqlOperation } from 'src/utils/amplify/legacy'
import { getErrorMessage } from 'src/utils/error'
import { fetchAllUserInfos } from 'src/utils/fetchUtils'
import { makeErrorToast, makeSuccessToast, msg } from 'src/utils/makeToast'
import {
	delayInMs,
	getEmailDomain,
	updateCompanyInfoWithDeltaSyncSuppress,
} from 'src/utils/miscellaneous'
import { captureExceptionAndThrow } from 'src/utils/posthogException'
import {
	costlineAccFamily,
	projectTypes as projectGroupTypes,
	quoteTypes as projectTypes,
	quoteTypes,
	vatIntracomType,
} from 'src/utils/typeUtils'
import { getProperUpdateValues } from 'src/utils/updateApiUtil'

type SettingsState = {
	companyInfo: ReduxCompany | null
	currencyRates: ReduxCurrencyRate[] | null
	userInformation: ReduxUserInfo | null
	companyNames: CompanyNameType[]
	commentsList: ReduxComments[]
	companyQuoteTemplateList?: CompanyQuoteTemplate[] | null
	usersOfCompany: ReduxUserInfo[]
	adminTour: any
	fetchCompanyInfoApiStatus: ApiStatus
	fetchCompanyNameApiStatus: ApiStatus
	fetchCurrencyRatesApiStatus: ApiStatus
	fetchTeamSettingsInfoApiStatus: ApiStatus
	updateCompanyInfoApiStatus: ApiStatus
	updateCompanyExportInfoApiStatus: ApiStatus
	createFreelancerInfoApiStatus: ApiStatus
	createUserInfoApiStatus: ApiStatus
	removeCompanyFavouriteProjectsInfoApiStatus: ApiStatus
	updateUserInfoApiStatus: ApiStatus
	createOrModifyCurrencyRateApiStatus: ApiStatus
	isMenuOpen: any | null
	commentSidebarOpen: boolean
	sort: string
	isUserProfileModalOpen: boolean
	isChangePasswordModalOpen: boolean
	isAccountSettingsOpen: boolean
	isImageUploadOpen: CompanyLogoImagesType | false
	isLogoutOpen: boolean
	tAndCFormStatus: { isOpen: boolean; section?: string | null }
	companyLogoUrl: string | null
	projectSelectDialogOpenStatus: boolean
	isUserMenuOpen: boolean
	isMenuDrawerOpen: boolean
	isChooseCompanyMenuOpen: boolean
	isMenuDrawerBudgetingOpen: boolean
	isMenuDrawerExpensesOpen: boolean
	isMenuDrawerRevenuesOpen: boolean
	isAdminWalkthroughClosed: boolean
	chooseSubscriptionModalStatus: boolean
	trialExpiredModalOpenStatus: boolean
	addNewUserModalOpenStatus: boolean
	subscriptionStatus: 'idle' | 'trial' | 'active' | 'expired'
	error?: string | null
}

const initialState: SettingsState = {
	companyInfo: null,
	currencyRates: null,
	userInformation: null,
	companyNames: [],
	usersOfCompany: [],
	adminTour: null,
	commentsList: [],
	companyQuoteTemplateList: [],
	fetchCompanyInfoApiStatus: 'idle',
	fetchCompanyNameApiStatus: 'idle',
	fetchCurrencyRatesApiStatus: 'idle',
	fetchTeamSettingsInfoApiStatus: 'idle',
	updateCompanyInfoApiStatus: 'idle',
	updateCompanyExportInfoApiStatus: 'idle',
	createFreelancerInfoApiStatus: 'idle',
	createUserInfoApiStatus: 'idle',
	removeCompanyFavouriteProjectsInfoApiStatus: 'idle',
	updateUserInfoApiStatus: 'idle',
	createOrModifyCurrencyRateApiStatus: 'idle',
	isMenuOpen: null,
	commentSidebarOpen: false,
	// Typescript requires to JSON.parse to get a string in it to parse so we manually provide string null to get the default sort of date when localstorage is empty for sort
	sort: JSON.parse(
		window.localStorage.getItem('sort') || JSON.stringify('date')
	),
	isUserProfileModalOpen: false,
	isChangePasswordModalOpen: false,
	isAccountSettingsOpen: false,
	isImageUploadOpen: false,
	isLogoutOpen: false,
	companyLogoUrl: null,
	projectSelectDialogOpenStatus: false,
	isUserMenuOpen: false,
	isMenuDrawerOpen: false,
	isChooseCompanyMenuOpen: false,
	isMenuDrawerBudgetingOpen: false,
	isMenuDrawerExpensesOpen: false,
	isMenuDrawerRevenuesOpen: false,
	isAdminWalkthroughClosed: false,
	tAndCFormStatus: { isOpen: false, section: null },
	trialExpiredModalOpenStatus: false,
	chooseSubscriptionModalStatus: false,
	addNewUserModalOpenStatus: false,
	subscriptionStatus: 'idle',
	error: null,
}

const throwSettingsError = (
	message: string,
	context: Record<string, unknown> = {}
): never => {
	return captureExceptionAndThrow(new Error(message), {
		location: 'settingsSlice',
		...context,
	})
}

const throwSettingsStatusError = (
	status: string,
	context: Record<string, unknown> = {}
): never => {
	return throwSettingsError(status || 'Unknown settings error', {
		status,
		...context,
	})
}

export const createCompanyInfo = createAsyncThunk<
	ReduxCompany,
	UserInfo,
	{ dispatch: ThunkAppDispatch; rejectValue: string }
>(
	'settings/createCompanyInfo',
	async (userInfo, { dispatch, rejectWithValue }) => {
		try {
			if (
				!userInfo.username ||
				!userInfo.currentCompanyTenantId ||
				!userInfo.id
			) {
				throwSettingsError(
					`UserInfo has incomplete info: ${userInfo}`,
					{
						action: 'createCompanyInfo',
						userId: userInfo.id,
						username: userInfo.username,
					}
				)
			}
			const { data: getUserInfo } = await dispatch(
				userInfoApi.endpoints.fetchUserInfo.initiate({
					userInfoId: userInfo.id!,
				})
			)
			const companyExists = (await API.graphql(
				graphqlOperation(queries.getCompany, {
					id: userInfo.currentCompanyTenantId,
				})
			)) as { data: { getCompany: ReduxCompany } }

			// Checks if company exists in db, if it does it return the company details else it creates the company
			if (companyExists.data.getCompany) {
				return companyExists.data.getCompany
			}

			const updateCompany: Partial<ReduxCompany> = {}
			for (const item of defaultValuesForCompanyTable) {
				const typedProperty = item.name as keyof ReduxCompany
				updateCompany[typedProperty] = item.value as any
			}

			const createdCompanyInfoDetails = {
				id: userInfo.currentCompanyTenantId,
				tenantId: userInfo.currentCompanyTenantId,
				...updateCompany,
			}
			const {
				data: { createCompany },
			} = (await API.graphql(
				graphqlOperation(mutations.createCompany, {
					input: createdCompanyInfoDetails,
				})
			)) as { data: { createCompany: ReduxCompany } }

			dispatch(createDefaultProjectsAndQuotes(userInfo))
			dispatch(createStripeInfo(userInfo))

			const emailDomain = getEmailDomain(userInfo.username as string)
			if (!createCompany) {
				throwSettingsError('createCompany is undefined', {
					action: 'createCompanyInfo',
					companyTenantId: userInfo.currentCompanyTenantId,
					username: userInfo.username,
				})
			}
			await API.graphql(
				graphqlOperation(mutations.createUniqueEmailId, {
					companyId: userInfo.currentCompanyTenantId,
					emailDomain,
				})
			)

			return createCompany
		} catch (err: any) {
			return rejectWithValue(err)
		}
	}
)

export const createDefaultProjectsAndQuotes = createAsyncThunk<
	void,
	UserInfo,
	{ dispatch: ThunkAppDispatch; rejectValue: string }
>(
	'settings/createDefaultProjectsAndQuotes',
	async (userInfo, { dispatch, rejectWithValue }) => {
		try {
			const tenantId = userInfo.currentCompanyTenantId
			if (!tenantId) {
				throwSettingsError(
					'No tenantId found in createDefaultProjectsAndQuotes',
					{
						action: 'createDefaultProjectsAndQuotes',
						userId: userInfo.id,
						username: userInfo.username,
					}
				)
			}
			const clientId = `dummyClient-${nanoid()}`
			const projectId = `dummyProject-${nanoid()}`
			const promiseArray = [] as any
			// Creates a new item in clients table save the id, name and tenantId that is the same as the user since only companyAdmin can create this
			const newClient = {
				id: clientId,
				name: 'Test Team',
				tenantId: tenantId,
				activeFlag: true,
				offlineIds: ['dummyId'],
				parentTeamId: clientId,
			} as ReduxClients

			promiseArray.push(
				API.graphql(
					graphqlOperation(mutations.createClients, {
						input: newClient,
					})
				)
			)

			const newProjectDetails = {
				id: projectId,
				name: 'Test Project',
				tenantId,
				projectGroup: 'other',
				activeFlag: true,
				type: 'project',
				freelancers: ['dummyId'],
				childFreelancers: ['dummyId'],
				offlineIds: ['dummyId'],
				isSubTeam: false,
				parentClientId: clientId,
				parentSubTeamId: projectId,
			} as CreateProjectsInput

			promiseArray.push(
				API.graphql(
					graphqlOperation(mutations.createProjects, {
						input: newProjectDetails,
					})
				)
			)

			promiseArray.push(
				await API.graphql(
					graphqlOperation(mutations.createQuoteFromS3, {
						tenantId: tenantId,
						parentProjectId: projectId,
						parentTeamId: clientId,
						parentSubTeamId: projectId,
						childFreelancers: ['dummyId'],
						quoteId: `${tenantId}-dummyQuote1-${nanoid().slice(
							0,
							5
						)}`,
						quoteJson: {
							quote: {
								Bucket: 'chedarv2staging205952-test/protected/customJson/dummyData',
								Key: 'apfp1/quote.json',
								identityId: 'dummyData',
							},
							costline: {
								Bucket: 'chedarv2staging205952-test/protected/customJson/dummyData',
								Key: 'apfp1/costline.json',
								identityId: 'dummyData',
							},
						},
					})
				)
			)
			promiseArray.push(
				await API.graphql(
					graphqlOperation(mutations.createQuoteFromS3, {
						tenantId: tenantId,
						parentProjectId: projectId,
						parentTeamId: clientId,
						parentSubTeamId: projectId,
						childFreelancers: ['dummyId'],
						quoteId: `${tenantId}-dummyQuote2-${nanoid().slice(
							0,
							5
						)}`,
						quoteJson: {
							quote: {
								Bucket: 'chedarv2staging205952-test/protected/customJson/dummyData',
								Key: 'apfp2/quote.json',
								identityId: 'dummyData',
							},
							costline: {
								Bucket: 'chedarv2staging205952-test/protected/customJson/dummyData',
								Key: 'apfp2/costline.json',
								identityId: 'dummyData',
							},
						},
					})
				)
			)
			//We create all the items in parallel to save time
			await Promise.all(promiseArray)

			dispatch(
				clientApi.util.invalidateTags([{ type: 'Client', id: 'LIST' }])
			)
		} catch (error) {
			console.error('error createDefaultProjectsAndQuotes: ', error)
			const message = getErrorMessage(error)
			makeErrorToast(message)
			return rejectWithValue(message)
		}
	}
)

export const createStripeInfo = createAsyncThunk<
	void,
	UserInfo,
	{ dispatch: ThunkAppDispatch; rejectValue: string }
>(
	'settings/createStripeInfo',
	async (userInfo, { dispatch, rejectWithValue }) => {
		try {
			const {
				username: email,
				currentCompanyTenantId: tenantId,
				currentCompanyName: name,
			} = userInfo
			const i18nextLanguage = i18next.language

			if (!email || !tenantId || !name || !i18nextLanguage) {
				console.log('i18nextLanguage: ', i18nextLanguage)
				console.log('name: ', name)
				console.log('tenantId: ', tenantId)
				console.log('email: ', email)
				return rejectWithValue(
					`Redux Incorrect data format, can't create customer`
				)
			}
			const { data } = await dispatch(
				subscriptionApi.endpoints.fetchCustomerData.initiate(tenantId)
			)
			if (data?.subscriptionData) {
				return
			}
			dispatch(
				subscriptionApi.endpoints.createStripeCustomer.initiate({
					email,
					tenantId,
					name,
					phoneNumber: userInfo.phone,
					preferred_locales: [i18nextLanguage],
				})
			)
		} catch (error) {
			console.error('error createStripeInfo: ', error)
			Sentry.captureException(
				'New user login, stripe customer could not be created'
			)
			const message = getErrorMessage(error)
			makeErrorToast(message)
			return rejectWithValue(message)
		}
	}
)

export const createUserInfo = createAsyncThunk<
	ReduxUserInfo,
	UserInfo,
	{ dispatch: ThunkAppDispatch; rejectValue: string }
>(
	'settings/createUserInfo',
	async (userInfo, { dispatch, rejectWithValue }) => {
		try {
			if (!userInfo?.attributes?.sub) {
				throwSettingsError(
					`userInfo is incomplete in createUserInfo: ${userInfo}`,
					{
						action: 'createUserInfo',
						userId: userInfo.id,
						username: userInfo.username,
					}
				)
			}
			// Check first if the user is present in the db
			const userExists = (await API.graphql(
				graphqlOperation(queries.getUserInfo, { id: userInfo.id })
			)) as { data: { getUserInfo: ReduxUserInfo } }
			//  If userExists then it exits the function else it creates the user in db
			if (userExists.data.getUserInfo) {
				return userExists.data.getUserInfo
			}
			const {
				data: { getCompany },
			} = (await API.graphql(
				graphqlOperation(queries.getCompany, {
					id: userInfo.currentCompanyTenantId,
				})
			)) as {
				data: { getCompany: ReduxCompany }
			}
			const [firstName, lastName] =
				userInfo?.attributes?.name?.split(' ') ?? []
			const createdUserInfoDetails: Partial<ReduxUserInfo> = {
				id: userInfo.id,
				email: userInfo.username,
				tenantId: userInfo.currentCompanyTenantId,
				currentCompanyTenantId: userInfo.currentCompanyTenantId,
				cognitoGroup: userInfo?.cognitoGroup,
				firstName: firstName ?? '',
				lastName: lastName ?? '',
				phone: userInfo.phone ?? '',
				hideTour: false,
			}
			if (getCompany?.name) {
				createdUserInfoDetails.currentCompanyName = getCompany?.name
			}
			if (userInfo?.groups?.includes('freelancer')) {
				createdUserInfoDetails.currentCompanyName = 'My Projects'
			}
			const {
				data: { createUserInfo },
			} = (await API.graphql(
				graphqlOperation(mutations.createUserInfo, {
					input: createdUserInfoDetails,
				})
			)) as { data: { createUserInfo: ReduxUserInfo } }

			dispatch(userInfoApi.util.invalidateTags([{ type: 'UserInfo' }]))

			return createUserInfo
		} catch (err: any) {
			return rejectWithValue(err)
		}
	}
)

export const fetchCompanyInfo = createAsyncThunk<
	ReduxCompany,
	undefined,
	{ state: RootState; rejectValue: string }
>('settings/fetchCompanyInfo', async (arg, { getState, rejectWithValue }) => {
	try {
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User table info is not set')
		}
		// We first query to get the latest _version number from db then update the model
		const {
			data: { getCompany },
		} = (await API.graphql(
			graphqlOperation(queries.getCompany, {
				id: userTableInfo.currentCompanyTenantId,
			})
		)) as {
			data: { getCompany: ReduxCompany }
		}
		return getCompany
	} catch (err: any) {
		return rejectWithValue(err)
	}
})

export const getCompanyNames = createAsyncThunk<
	CompanyNameType[] | [],
	string[],
	{ rejectValue: string }
>('settings/getCompanyNames', async (companyIds, { rejectWithValue }) => {
	try {
		const promiseArray = [] as any
		for (const id of companyIds) {
			promiseArray.push(
				API.graphql(graphqlOperation(queries.getCompany, { id }))
			)
		}
		const result = await Promise.all(promiseArray)
		const companyNames = [] as CompanyNameType[]
		for (const r of result) {
			if (r.data.getCompany.name) {
				companyNames.push({
					tenantId: r.data.getCompany.id,
					name: r.data.getCompany.name,
				})
			}
		}
		return companyNames
	} catch (err: any) {
		return rejectWithValue(err)
	}
})

export const fetchUserInfo = createAsyncThunk<
	ReduxUserInfo,
	undefined,
	{ state: RootState; rejectValue: string }
>('settings/fetchUserInfo', async (data, { getState, rejectWithValue }) => {
	try {
		const { auth } = getState()
		const userInfo = auth.userInfo
		// We first query to get the latest _version number from db then update the model
		const {
			data: { getUserInfo },
		} = (await API.graphql(
			graphqlOperation(queries.getUserInfo, { id: userInfo.id })
		)) as {
			data: { getUserInfo: ReduxUserInfo }
		}
		return getUserInfo
	} catch (err: any) {
		return rejectWithValue(err)
	}
})

export const fetchTeamSettingsInfo = createAsyncThunk<
	ReduxUserInfo[],
	undefined,
	{ state: RootState; rejectValue: string }
>(
	'settings/fetchTeamSettingsInfo',
	async (data, { getState, rejectWithValue }) => {
		try {
			const { auth } = getState()
			const userInfo = auth.userInfo
			if (!userInfo.currentCompanyTenantId) {
				return rejectWithValue('No current company tenant id')
			}
			const userInfoDetails: ReduxUserInfo[] = await fetchAllUserInfos()
			const filterUserInfo = userInfoDetails
				?.filter(
					(u) =>
						userInfo.currentCompanyTenantId &&
						(u?.companyTenantId?.includes(
							userInfo.currentCompanyTenantId
						) ||
							u.currentCompanyTenantId ===
								userInfo.currentCompanyTenantId)
				)
				?.filter((u) => {
					const email: string = u?.email?.toString().toLowerCase()
					return !email?.endsWith('@chedar.fr')
				})
			return filterUserInfo
		} catch (err: any) {
			return rejectWithValue(err)
		}
	}
)

interface UpdateCompanyArg extends Partial<ReduxCompany> {
	currency?: string
	termsAndCondition3?: string | null | undefined
	termsAndCondition4?: string | null | undefined
	vatAccount?: string | null | undefined
	costAccountNumbers?: string | null | undefined
	costCategoriesFields?: any
	costVatFields?: any
	miscAccNosFields?: any
	date?: any
	advertisingProductionName?: string
	frenchApfpName?: string
	frenchApfpCount?: string
	frenchApfpEUName?: string
	frenchPostProdName?: string
	frenchPostProdCount?: string
	aicpName?: string
	aicpCount?: string
	photoGridName?: string
	photoGridCount?: string
	euDebitNumber?: string
	euDebitName?: string
	euCreditNumber?: string
	euCreditName?: string
	intlDebitNumber?: string
	intlDebitName?: string
	intlCreditNumber?: string
	intlCreditName?: string
	servicesProvided20LocalName?: string
	seperator?: string
	first?: string
	second?: string
	third?: string
	preview?: string
	quoteseperator?: string
	quotefirst?: string
	quotesecond?: string
	quotethird?: string
	quotepreview?: string
}
type VatAccNos = {
	number: string | null | undefined
	name: string | null | undefined
	text: string | null | undefined
}
type MiscAccNos = {
	number: string | null | undefined
	name: string | null | undefined
	text?: string | null | undefined
}
type CostAccNos = {
	id: string | null | undefined
	name: string | null | undefined
}

export const updateCompanyInfo = createAsyncThunk<
	ReduxCompany,
	UpdateCompanyArg,
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'settings/updateCompanyInfo',
	async (arg, { getState, rejectWithValue, dispatch }) => {
		try {
			const { auth } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo?.currentCompanyTenantId) {
				throwSettingsError('Current company tenant id is not set', {
					action: 'updateCompanyInfo',
					userId: userTableInfo?.id,
				})
			}
			// We first query to get the latest _version number from db then update the model
			const {
				data: { getCompany },
			} = (await API.graphql(
				graphqlOperation(queries.getCompany, {
					id: userTableInfo?.currentCompanyTenantId,
				})
			)) as {
				data: { getCompany: ReduxCompany }
			}
			console.log('arg: ', arg)
			delete arg.termsAndCondition3
			delete arg.termsAndCondition4
			delete arg.vatAccount
			delete arg.costAccountNumbers
			if (arg?.costCategoriesFields) {
				const newCategoryFields = arg?.costCategoriesFields.map(
					(arg: CostAccNos) => {
						return { ...arg, name: `${arg?.id} - ${arg?.name}` }
					}
				)
				const companyInfo = getCompany
				if (
					companyInfo?.costCategoriesFields &&
					companyInfo?.costCategoriesFields.length > 0
				) {
					arg.costCategoriesFields = [
						...(companyInfo?.costCategoriesFields ?? []),
						...newCategoryFields,
					]
				} else {
					arg.costCategoriesFields = newCategoryFields
				}
			}
			if (arg?.costVatFields) {
				const newVatFields = arg?.costVatFields.map(
					(arg: VatAccNos) => {
						return { ...arg, name: `${arg?.number} - ${arg?.text}` }
					}
				)
				const companyInfo = getCompany
				if (
					companyInfo?.costVatFields &&
					companyInfo?.costVatFields.length > 0
				) {
					arg.costVatFields = [
						...(companyInfo?.costVatFields ?? []),
						...newVatFields,
					]
				} else {
					arg.costVatFields = newVatFields
				}
			}
			if (arg?.miscAccNosFields) {
				const newMiscFields = arg?.miscAccNosFields.map(
					(arg: MiscAccNos) => {
						return { ...arg, text: `${arg?.number} - ${arg?.name}` }
					}
				)
				arg.miscAccNosFields = newMiscFields
				if (
					arg?.miscAccNosFields?.length === 1 &&
					arg?.miscAccNosFields[0]?.number === '' &&
					arg?.miscAccNosFields[0]?.name === ''
				) {
					arg.miscAccNosFields = []
				}
			}
			const projectCount = getCompany.projectCount
			let quoteCount = getCompany.quoteCount
			let projectGroupCount = getCompany.projectGroupCount
			let salesAccQuoteTypeDetails = getCompany.salesAccQuoteTypeDetails
			let salesAccByCostlineDetails = getCompany.salesAccByCostlineDetails

			let vatIntracom = getCompany.vatIntracom
			let projectNumberCustomisation =
				getCompany.projectNumberCustomisation
			let quoteNumberCustomisation = getCompany.quoteNumberCustomisation
			//* Temporary fix checking if arg.frenchApfName  for project Count or frenchApfpEUName for sales acc by quotetype is present and only then updating the array values below
			// * In future check if you can convert these to useFieldArray values in react hook form
			console.log('arg1: ', arg)
			// if (arg.frenchApfpName || arg.frenchApfpEUName) {
			//   console.log('arg2: ', arg)
			//   projectCount = projectTypes.map(pt => {
			//     type ObjectKey = keyof typeof arg
			//     const name = `${pt}Name` as ObjectKey
			//     const count = `${pt}Count` as ObjectKey
			//     const projectTypeDetails = {
			//       type: pt,
			//       year: dayjs().toISOString(),
			//       name: arg[name] as string,
			//       count: parseInt(arg[count] as string),
			//     }
			//     delete arg[name]
			//     delete arg[count]
			//     return projectTypeDetails
			//   })
			// }
			if (arg.advertisingProductionName) {
				projectGroupCount = projectGroupTypes.map((pt) => {
					type ObjectKey = keyof typeof arg
					const name = `${pt}Name` as ObjectKey
					const count = `${pt}Count` as ObjectKey
					const projectTypeDetails = {
						type: pt,
						year: dayjs().toISOString(),
						name: arg[name] as string,
						count: Number.parseInt(arg[count] as string),
					}
					delete arg[name]
					delete arg[count]
					return projectTypeDetails
				})
			}
			if (arg.frenchApfpName) {
				quoteCount = quoteTypes.map((pt) => {
					type ObjectKey = keyof typeof arg
					const name = `${pt}Name` as ObjectKey
					const count = `${pt}Count` as ObjectKey
					const quoteTypeDetails = {
						type: pt,
						year: dayjs().toISOString(),
						name: arg[name] as string,
						count: Number.parseInt(arg[count] as string),
					}
					delete arg[name]
					delete arg[count]
					return quoteTypeDetails
				})
			}
			console.log('Initial Check: ', arg.servicesProvided20LocalName)

			// * Convert these to field array values later
			if (
				arg.servicesProvided20LocalName ||
				arg.servicesProvided20LocalName === ''
			) {
				salesAccQuoteTypeDetails = projectTypes.map((pt) => {
					type ObjectKey = keyof typeof arg
					const LocalName = `${pt}LocalName` as ObjectKey
					const LocalNumber = `${pt}LocalNumber` as ObjectKey
					const EUName = `${pt}EUName` as ObjectKey
					const EUNumber = `${pt}EUNumber` as ObjectKey
					const InternationalName =
						`${pt}InternationalName` as ObjectKey
					const InternationalNumber =
						`${pt}InternationalNumber` as ObjectKey
					const salesAccQuoteTypeDetails = {
						type: pt,
						EUName: arg[EUName],
						EUNumber: arg[EUNumber],
						InternationalName: arg[InternationalName],
						InternationalNumber: arg[InternationalNumber],
						LocalName: arg[LocalName],
						LocalNumber: arg[LocalNumber],
					}
					delete arg[LocalName]
					delete arg[LocalNumber]
					delete arg[EUName]
					delete arg[EUNumber]
					delete arg[InternationalName]
					delete arg[InternationalNumber]
					return salesAccQuoteTypeDetails
				})
			}
			// * Convert these to field array values later
			if (
				arg.servicesProvided20LocalName ||
				arg.servicesProvided20LocalName === ''
			) {
				salesAccByCostlineDetails = costlineAccFamily.map((pt) => {
					type ObjectKey = keyof typeof arg
					const LocalName = `${pt}LocalName` as ObjectKey
					const LocalNumber = `${pt}LocalNumber` as ObjectKey
					const EUName = `${pt}EUName` as ObjectKey
					const EUNumber = `${pt}EUNumber` as ObjectKey
					const InternationalName =
						`${pt}InternationalName` as ObjectKey
					const InternationalNumber =
						`${pt}InternationalNumber` as ObjectKey
					const salesAccByCostlineDetails = {
						id: pt,
						EUName: arg[EUName],
						EUNumber: arg[EUNumber],
						InternationalName: arg[InternationalName],
						InternationalNumber: arg[InternationalNumber],
						LocalName: arg[LocalName],
						LocalNumber: arg[LocalNumber],
					}
					delete arg[LocalName]
					delete arg[LocalNumber]
					delete arg[EUName]
					delete arg[EUNumber]
					delete arg[InternationalName]
					delete arg[InternationalNumber]
					return salesAccByCostlineDetails
				})
			}

			// Check if arg is there to see if vat intracom is being updated, else it will get updated from every tab
			// Only need to check one, if it is undefined
			if (arg.euDebitNumber || arg.euDebitNumber === '') {
				vatIntracom = vatIntracomType.map((vt) => {
					type ObjectKey = keyof typeof arg
					const debitNumber = `${vt}DebitNumber` as ObjectKey
					const debitName = `${vt}DebitName` as ObjectKey
					const creditNumber = `${vt}CreditNumber` as ObjectKey
					const creditName = `${vt}CreditName` as ObjectKey

					const vatIntracomDetails = {
						type: vt,
						debitNumber: arg[debitNumber],
						debitName: arg[debitName],
						creditNumber: arg[creditNumber],
						creditName: arg[creditName],
					}
					delete arg[debitNumber]
					delete arg[debitName]
					delete arg[creditNumber]
					delete arg[creditName]
					return vatIntracomDetails
				})
			}
			if (arg.invoiceCount) {
				arg.invoiceCount = Number.parseInt(`${arg.invoiceCount}`)
			}
			if (arg.seperator || arg.seperator === '') {
				projectNumberCustomisation = [
					{
						seperator: arg.seperator,
						first: arg.first,
						second: arg.second,
						third: arg.third,
					},
				]
				delete arg.seperator
				delete arg.first
				delete arg.second
				delete arg.third
				delete arg.preview
			}
			if (arg.quoteseperator || arg.quoteseperator === '') {
				quoteNumberCustomisation = [
					{
						seperator: arg.quoteseperator,
						first: arg.quotefirst,
						second: arg.quotesecond,
						third: arg.quotethird,
					},
				]
				delete arg.quoteseperator
				delete arg.quotefirst
				delete arg.quotesecond
				delete arg.quotethird
				delete arg.quotepreview
			}
			const { currency, ...updatedArg } = arg
			console.log('arg: ', arg)
			// Updated userInfo details
			const updatedCompanyInfoDetails = getProperUpdateValues({
				updatedInput: {
					projectGroupCount,
					quoteCount,
					// projectCount,
					salesAccQuoteTypeDetails,
					salesAccByCostlineDetails,
					vatIntracom,
					projectNumberCustomisation,
					quoteNumberCustomisation,
					...updatedArg,
				},
				originalInput: getCompany,
			})
			console.log(
				'updatedCompanyInfoDetails: ',
				updatedCompanyInfoDetails
			)
			// ! Check why this was necessary
			if (updatedCompanyInfoDetails.invoiceCount) {
				updatedCompanyInfoDetails.invoicingYear = dayjs().toISOString()
			}
			// // delete currency with different key name
			// delete updatedCompanyInfoDetails.currency
			const updateCompany = await updateCompanyInfoWithDeltaSyncSuppress(
				updatedCompanyInfoDetails
			)
			if (
				arg.accountCurrency &&
				arg.accountCurrency !== getCompany.accountCurrency
			) {
				dispatch(
					createOrModifyCurrencyRate({
						userInfo: userTableInfo!,
						newBaseCurrency: arg.accountCurrency,
					})
				)
			}

			//! Remove this when no longer required to fetch state from redux
			dispatch(fetchCompanyInfo())
			if (userTableInfo?.currentCompanyTenantId) {
				dispatch(
					companyApi.util.invalidateTags([
						{
							type: 'Company',
							id: userTableInfo?.currentCompanyTenantId,
						},
					])
				)
			}

			return updateCompany
		} catch (err: any) {
			return rejectWithValue(err)
		}
	}
)

export const updateCompanyFavouriteProjectsInfo = createAsyncThunk<
	ReduxCompany,
	FavouriteProject,
	{ state: RootState; rejectValue: string }
>(
	'settings/updateCompanyFavouriteProjectsInfo',
	async (data, { getState, rejectWithValue }) => {
		try {
			const { auth } = getState()

			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User table info is not set')
			}
			if (!userTableInfo.currentCompanyTenantId) {
				throwSettingsError('Current company tenant id is not set', {
					action: 'updateCompanyFavouriteProjectsInfo',
					userId: userTableInfo.id,
				})
				throw new Error('Current company tenant id is not set')
			}
			// We first query to get the latest _version number from db then update the model
			const {
				data: { getCompany },
			} = (await API.graphql(
				graphqlOperation(queries.getCompany, {
					id: userTableInfo.currentCompanyTenantId,
				})
			)) as {
				data: { getCompany: ReduxCompany }
			}

			// Updated userInfo details
			const updatedCompanyInfoDetails = {
				id: userTableInfo.currentCompanyTenantId,
				favouriteProjects: [
					...(getCompany?.favouriteProjects || []),
					data,
				],
			}

			const updateCompany = await updateCompanyInfoWithDeltaSyncSuppress(
				updatedCompanyInfoDetails
			)

			makeSuccessToast('Information successfully updated')

			return updateCompany
		} catch (err: any) {
			return rejectWithValue(err)
		}
	}
)

export const removeCompanyFavouriteProjectsInfo = createAsyncThunk<
	ReduxCompany,
	string | null | undefined,
	{ state: RootState; rejectValue: string }
>(
	'settings/removeCompanyFavouriteProjectsInfo',
	async (data, { getState, rejectWithValue }) => {
		console.log('data: ', data)
		try {
			const { auth } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User table info is not set')
			}
			if (!userTableInfo.currentCompanyTenantId) {
				throwSettingsError('Current company tenant id is not set', {
					action: 'removeCompanyFavouriteProjectsInfo',
					userId: userTableInfo.id,
					projectId: data,
				})
				throw new Error('Current company tenant id is not set')
			}
			// We first query to get the latest _version number from db then update the model
			const {
				data: { getCompany },
			} = (await API.graphql(
				graphqlOperation(queries.getCompany, {
					id: userTableInfo.currentCompanyTenantId,
				})
			)) as {
				data: { getCompany: ReduxCompany }
			}

			const favProjectsArray = getCompany.favouriteProjects
			if (!favProjectsArray) {
				throwSettingsError(
					`Favorite Projects Array is empty or undefined favProjectsArray:${favProjectsArray}`,
					{
						action: 'removeCompanyFavouriteProjectsInfo',
						userId: userTableInfo.id,
						projectId: data,
					}
				)
				throw new Error(
					`Favorite Projects Array is empty or undefined favProjectsArray:${favProjectsArray}`
				)
			}
			let newFavProjects = favProjectsArray
			if (data) {
				newFavProjects = favProjectsArray?.filter(
					(pa) => pa?.id !== data
				)
			}
			// Updated userInfo details
			const updatedCompanyInfoDetails = {
				id: userTableInfo.currentCompanyTenantId,
				favouriteProjects: newFavProjects,
			}

			const updateCompany = await updateCompanyInfoWithDeltaSyncSuppress(
				updatedCompanyInfoDetails
			)

			makeSuccessToast('Information successfully updated')
			return updateCompany
		} catch (err: any) {
			return rejectWithValue(err)
		}
	}
)

export const updateCompanyExportInfo = createAsyncThunk<
	void,
	{ type: string },
	{ state: RootState; rejectValue: string }
>(
	'settings/updateCompanyExportInfo',
	async ({ type }, { getState, rejectWithValue }) => {
		try {
			const { auth } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User table info is not set')
			}

			if (!userTableInfo.currentCompanyTenantId) {
				throwSettingsError('Current company tenant id is not set', {
					action: 'updateCompanyExportInfo',
					userId: userTableInfo.id,
					exportType: type,
				})
			}

			const newExportInfoObject = {
				id: nanoid(),
				tenantId: userTableInfo.currentCompanyTenantId,
				activeFlag: true,
				name: userTableInfo.email,
				exportDate: dayjs().toISOString(),
				exportType: type,
			}
			const {
				data: { createExportInfo },
			} = (await API.graphql(
				graphqlOperation(mutations.createExportInfo, {
					input: newExportInfoObject,
				})
			)) as { data: { createExportInfo: ReduxExportInfo } }
		} catch (err: any) {
			return rejectWithValue(err)
		}
	}
)

export const updateUserInfo = createAsyncThunk<
	ReduxUserInfo,
	Partial<ReduxUserInfo>,
	{ state: RootState; rejectValue: string }
>('settings/updateUserInfo', async (data, { getState, rejectWithValue }) => {
	try {
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User table info is not set')
		}
		// We first query to get the latest _version number from db then update the model
		const {
			data: { getUserInfo },
		} = (await API.graphql(
			graphqlOperation(queries.getUserInfo, { id: userTableInfo.id })
		)) as {
			data: { getUserInfo: ReduxUserInfo }
		}
		// Updated userInfo details
		const updatedUserInfoDetails = {
			id: userTableInfo.id,
			...data,
		}
		const {
			data: { updateUserInfo },
		} = (await API.graphql(
			graphqlOperation(mutations.updateUserInfo, {
				input: updatedUserInfoDetails,
			})
		)) as { data: { updateUserInfo: ReduxUserInfo } }
		i18next.changeLanguage(updateUserInfo?.userLanguage ?? 'fr')
		return updateUserInfo
	} catch (err: any) {
		const message = getErrorMessage(err)
		console.error('updateUserInfo redux error: ', message)
		makeErrorToast(msg)
		return rejectWithValue(err)
	}
})

export const createFreelancerInfo = createAsyncThunk<
	void,
	{ userInfo: UserInfo; email: string },
	{ dispatch: ThunkAppDispatch; rejectValue: string }
>(
	'settings/createFreelancerInfo',
	async ({ userInfo, email }, { dispatch, rejectWithValue }) => {
		try {
			const {
				data: {
					freelancersById: { items },
				},
			} = (await API.graphql(
				graphqlOperation(queries.freelancersById, {
					freelancerTenantId: userInfo.tenantId,
				})
			)) as { data: { freelancersById: { items: ReduxFreelancers[] } } }

			if (
				userInfo?.groups?.includes('freelancer') &&
				items.length === 0
			) {
				const newItem: Partial<ReduxFreelancers> = {
					id: userInfo.id,
					freelancerTenantId: userInfo.tenantId,
					email: email.toLowerCase(),
					currentCompanyTenantId: userInfo.tenantId,
					currentCompanyName: 'My Projects',
				}
				try {
					await API.graphql(
						graphqlOperation(mutations.createFreelancers, {
							input: newItem,
						})
					)
					await delayInMs(2000)
					dispatch(
						freelancersApi.util.invalidateTags([
							{ type: 'Freelancers' },
						])
					)
				} catch (err: any) {
					makeErrorToast(msg)
				}
			}
		} catch (err: any) {
			const message = getErrorMessage(err)
			console.error('createFreelancerInfo redux error: ', message)
			makeErrorToast(err.message)
			return rejectWithValue(err)
		}
	}
)

export const fetchCurrencyRates = createAsyncThunk<
	ReduxCurrencyRate[],
	undefined,
	{ state: RootState; rejectValue: string }
>('settings/fetchCurrencyRates', async (arg, { getState, rejectWithValue }) => {
	try {
		const { auth } = getState()
		const userInfo = auth.userInfo
		// We first query to get the latest _version number from db then update the model
		const {
			data: {
				currencyRateByTenantId: { items: currencyRate },
			},
		} = (await API.graphql(
			graphqlOperation(queries.currencyRateByTenantId, {
				tenantId: userInfo.currentCompanyTenantId,
			})
		)) as {
			data: { currencyRateByTenantId: { items: ReduxCurrencyRate[] } }
		}

		return currencyRate
	} catch (err: any) {
		return rejectWithValue(err)
	}
})

export const createOrModifyCurrencyRate = createAsyncThunk<
	void,
	{
		userInfo: ReduxUserInfo
		newBaseCurrency?: string
	},
	{ state: RootState; rejectValue: string }
>(
	'settings/createOrModifyCurrencyRate',
	async (
		{ userInfo, newBaseCurrency },
		{ getState, rejectWithValue, dispatch }
	) => {
		let errorMessage = msg

		const tenantId = userInfo.currentCompanyTenantId
		try {
			const {
				data: {
					createOrUpdateCurrency: { status, isSuccessful },
				},
			} = (await API.graphql(
				graphqlOperation(mutations.createOrUpdateCurrency, {
					companyTenantId: tenantId,
					newBaseCurrency,
				})
			)) as {
				data: {
					createOrUpdateCurrency: {
						status: string
						isSuccessful: boolean
					}
				}
			}
			if (isSuccessful) {
				dispatch(fetchCurrencyRates())
			} else {
				if (status !== 'Failed') {
					errorMessage = status
				}
				throwSettingsStatusError(status, {
					action: 'createOrModifyCurrencyRate',
					companyTenantId: tenantId,
					newBaseCurrency,
				})
			}
		} catch (err: any) {
			makeErrorToast(`Error : ${errorMessage}`)
			return rejectWithValue(err)
		}
	}
)

export const resetInvoiceCount = createAsyncThunk<
	void,
	undefined,
	{ state: RootState; rejectValue: string }
>('settings/resetInvoiceCount', async (arg, { getState, rejectWithValue }) => {
	try {
		const { auth } = getState()
		const userInfo = auth.userInfo
		// We first query to get the latest _version number from db then update the model
		const {
			data: { getCompany },
		} = (await API.graphql(
			graphqlOperation(queries.getCompany, {
				id: userInfo.currentCompanyTenantId,
			})
		)) as {
			data: { getCompany: ReduxCompany }
		}

		const zeroPad = (num: number, places: number) =>
			String(num).padStart(places, '0')
		let newInvoicingYear
		let newInvoiceNumber

		const today = dayjs().year()
		const oldInvoicingYear = dayjs(getCompany.invoicingYear).year()
		if (today > oldInvoicingYear) {
			newInvoicingYear = dayjs().toISOString()
			newInvoiceNumber = 0
			const updateCompany = {
				id: getCompany.id,
				invoiceCount: newInvoiceNumber,
				invoicingYear: newInvoicingYear,
			}
			await updateCompanyInfoWithDeltaSyncSuppress(updateCompany)
		}
	} catch (err: any) {
		return rejectWithValue(err)
	}
})

export const settingsSlice = createSlice({
	name: 'settings',
	initialState,
	reducers: {
		setIsMenuOpen: (state, action) => {
			state.isMenuOpen = action.payload
		},
		setCommentSideBarOpen: (state, action) => {
			state.commentSidebarOpen = action.payload
		},
		setSortBy: (state, action) => {
			window.localStorage.setItem('sort', JSON.stringify(action.payload))
			state.sort = action.payload
		},
		setIsUserProfileModalOpen: (state, action) => {
			state.isUserProfileModalOpen = action.payload
		},
		setIsChangePasswordModalOpen: (state, action) => {
			state.isChangePasswordModalOpen = action.payload
		},
		setIsAccountSettingsOpen: (state, action) => {
			state.isAccountSettingsOpen = action.payload
		},
		setIsImageUploadOpen: (state, action) => {
			state.isImageUploadOpen = action.payload
		},
		setIsLogoutOpen: (state, action) => {
			state.isLogoutOpen = action.payload
		},
		setCompanyLogoUrl: (state, action) => {
			state.companyLogoUrl = action.payload
		},
		setCompanyInfo: (state, action) => {
			state.companyInfo = action.payload
		},
		setProjectSelectDialogOpenStatus: (state, action) => {
			state.projectSelectDialogOpenStatus = action.payload
		},
		setTAndCFormStatus: (state, action) => {
			state.tAndCFormStatus = action.payload
		},
		setIsUserMenuOpen: (state, action) => {
			state.isUserMenuOpen = action.payload
		},
		setIsChooseCompanyMenuOpen: (state, action) => {
			state.isChooseCompanyMenuOpen = action.payload
		},
		setIsMenuDrawerOpen: (state, action) => {
			state.isMenuDrawerOpen = action.payload
		},
		setIsMenuDrawerBudgetingOpen: (state, action) => {
			state.isMenuDrawerBudgetingOpen = action.payload
		},
		setIsMenuDrawerExpensesOpen: (state, action) => {
			state.isMenuDrawerExpensesOpen = action.payload
		},
		setIsMenuDrawerRevenuesOpen: (state, action) => {
			state.isMenuDrawerRevenuesOpen = action.payload
		},
		setAdminTour: (state, action) => {
			state.adminTour = action.payload
		},
		setIsAdminWalkthroughClosed: (state, action) => {
			state.isAdminWalkthroughClosed = action.payload
		},
		setTrialExpiredModalStatus: (state, action) => {
			state.trialExpiredModalOpenStatus = action.payload
		},
		setChooseSubscriptionModalStatus: (state, action) => {
			state.chooseSubscriptionModalStatus = action.payload
		},
		setAddNewUserModalStatus: (state, action) => {
			state.addNewUserModalOpenStatus = action.payload
		},
		appendCompanyName: (state, action: PayloadAction<CompanyNameType>) => {
			const exists = state.companyNames.some(
				(company) => company.tenantId === action.payload.tenantId
			)
			if (!exists) {
				state.companyNames.push(action.payload)
			}
		},
		setSubscriptionStatus: (state, action) => {
			state.subscriptionStatus = action.payload
		},
	},
	extraReducers: (builder) => {
		builder.addCase(createCompanyInfo.fulfilled, (state, action) => {
			state.companyInfo = action.payload
		})
		builder.addCase(createCompanyInfo.rejected, (state, action) => {
			state.error = action.error.message
		})
		builder.addCase(createUserInfo.pending, (state, action) => {
			state.createUserInfoApiStatus = 'loading'
		})
		builder.addCase(createUserInfo.fulfilled, (state, action) => {
			state.userInformation = action.payload
			state.createUserInfoApiStatus = 'succeeded'
		})
		builder.addCase(createUserInfo.rejected, (state, action) => {
			state.error = action.error.message
			state.createUserInfoApiStatus = 'succeeded'
		})
		builder.addCase(fetchCompanyInfo.pending, (state, action) => {
			state.fetchCompanyInfoApiStatus = 'loading'
		})
		builder.addCase(fetchCompanyInfo.fulfilled, (state, action) => {
			state.companyInfo = action.payload
			state.fetchCompanyInfoApiStatus = 'succeeded'
		})
		builder.addCase(fetchCompanyInfo.rejected, (state, action) => {
			state.error = action.error.message
			state.fetchCompanyInfoApiStatus = 'failed'
		})
		builder.addCase(fetchCurrencyRates.pending, (state, action) => {
			state.fetchCurrencyRatesApiStatus = 'loading'
		})
		builder.addCase(fetchCurrencyRates.fulfilled, (state, action) => {
			state.currencyRates = action.payload
			state.fetchCurrencyRatesApiStatus = 'succeeded'
		})
		builder.addCase(fetchCurrencyRates.rejected, (state, action) => {
			state.error = action.error.message
			state.fetchCurrencyRatesApiStatus = 'failed'
		})
		builder.addCase(fetchTeamSettingsInfo.pending, (state, action) => {
			state.fetchTeamSettingsInfoApiStatus = 'loading'
		})
		builder.addCase(fetchTeamSettingsInfo.fulfilled, (state, action) => {
			state.usersOfCompany = action.payload
			state.fetchTeamSettingsInfoApiStatus = 'succeeded'
		})
		builder.addCase(fetchTeamSettingsInfo.rejected, (state, action) => {
			state.error = action.error.message
			state.fetchTeamSettingsInfoApiStatus = 'failed'
		})
		builder.addCase(fetchUserInfo.fulfilled, (state, action) => {
			state.userInformation = action.payload
		})
		builder.addCase(fetchUserInfo.rejected, (state, action) => {
			state.error = action.error.message
		})
		builder.addCase(getCompanyNames.pending, (state, action) => {
			state.fetchCompanyNameApiStatus = 'loading'
		})
		builder.addCase(getCompanyNames.fulfilled, (state, action) => {
			state.fetchCompanyNameApiStatus = 'succeeded'
			state.companyNames = action.payload
		})
		builder.addCase(updateCompanyInfo.pending, (state, action) => {
			state.updateCompanyInfoApiStatus = 'loading'
		})
		builder.addCase(updateCompanyInfo.fulfilled, (state, action) => {
			state.updateCompanyInfoApiStatus = 'succeeded'
			state.companyInfo = action.payload
			state.tAndCFormStatus = { isOpen: false, section: null }
			if (!action.meta.arg.logoImage) {
				state.isAccountSettingsOpen = false
				makeSuccessToast('Information successfully updated')
			}
		})
		builder.addCase(updateCompanyInfo.rejected, (state, action) => {
			state.updateCompanyInfoApiStatus = 'failed'
			state.tAndCFormStatus = { isOpen: false, section: null }
			state.error = action.error.message
		})
		builder.addCase(
			updateCompanyFavouriteProjectsInfo.pending,
			(state, action) => {
				state.updateCompanyInfoApiStatus = 'loading'
			}
		)
		builder.addCase(
			updateCompanyFavouriteProjectsInfo.fulfilled,
			(state, action) => {
				state.updateCompanyInfoApiStatus = 'succeeded'
				state.projectSelectDialogOpenStatus = false
				state.companyInfo = action.payload
			}
		)
		builder.addCase(
			updateCompanyFavouriteProjectsInfo.rejected,
			(state, action) => {
				state.updateCompanyInfoApiStatus = 'failed'
				state.error = action.error.message
			}
		)
		builder.addCase(
			removeCompanyFavouriteProjectsInfo.pending,
			(state, action) => {
				state.removeCompanyFavouriteProjectsInfoApiStatus = 'loading'
			}
		)
		builder.addCase(
			removeCompanyFavouriteProjectsInfo.fulfilled,
			(state, action) => {
				state.removeCompanyFavouriteProjectsInfoApiStatus = 'succeeded'
				state.companyInfo = action.payload
			}
		)
		builder.addCase(
			removeCompanyFavouriteProjectsInfo.rejected,
			(state, action) => {
				state.removeCompanyFavouriteProjectsInfoApiStatus = 'failed'
				state.error = action.error.message
			}
		)
		builder.addCase(updateCompanyExportInfo.pending, (state, action) => {
			state.updateCompanyExportInfoApiStatus = 'loading'
		})
		builder.addCase(updateCompanyExportInfo.fulfilled, (state, action) => {
			state.updateCompanyExportInfoApiStatus = 'succeeded'
		})
		builder.addCase(updateCompanyExportInfo.rejected, (state, action) => {
			state.updateCompanyExportInfoApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(createFreelancerInfo.pending, (state, action) => {
			state.createFreelancerInfoApiStatus = 'loading'
		})
		builder.addCase(createFreelancerInfo.fulfilled, (state, action) => {
			state.createFreelancerInfoApiStatus = 'succeeded'
		})
		builder.addCase(createFreelancerInfo.rejected, (state, action) => {
			state.updateCompanyExportInfoApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(updateUserInfo.pending, (state, action) => {
			state.updateUserInfoApiStatus = 'loading'
		})
		builder.addCase(updateUserInfo.fulfilled, (state, action) => {
			state.updateUserInfoApiStatus = 'succeeded'
			makeSuccessToast('Information successfully updated')
			state.isUserProfileModalOpen = false
			state.userInformation = action.payload
		})
		builder.addCase(updateUserInfo.rejected, (state, action) => {
			state.updateUserInfoApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(createOrModifyCurrencyRate.pending, (state, action) => {
			state.createOrModifyCurrencyRateApiStatus = 'loading'
		})
		builder.addCase(
			createOrModifyCurrencyRate.fulfilled,
			(state, action) => {
				state.createOrModifyCurrencyRateApiStatus = 'succeeded'
			}
		)
		builder.addCase(
			createOrModifyCurrencyRate.rejected,
			(state, action) => {
				state.createOrModifyCurrencyRateApiStatus = 'failed'
				state.error = action.error.message
			}
		)
		builder.addMatcher(
			companyApi.endpoints.fetchCompanyById.matchFulfilled,
			(state, action) => {
				state.companyInfo = action.payload
			}
		)
		// Gets the comments from the rest api and sets them in the state
		builder.addMatcher(
			quoteRestApi.endpoints.fetchRestQuoteById.matchFulfilled,
			(state, action) => {
				state.commentsList = action.payload.commentsArray?.sort(
					(a, b) => {
						return (
							new Date(a.createdAt).getTime() -
							new Date(b.createdAt).getTime()
						)
					}
				)
			}
		)
		// Gets the comments from the authenticated graphql api and sets them in the state
		builder.addMatcher(
			commentsApi.endpoints.fetchCommentsByQuoteId.matchFulfilled,
			(state, action) => {
				state.commentsList = action.payload.sort((a, b) => {
					return (
						new Date(a.createdAt).getTime() -
						new Date(b.createdAt).getTime()
					)
				})
			}
		)
		builder.addMatcher(
			subscriptionApi.endpoints.fetchCustomerData.matchFulfilled,
			(state, action) => {
				if (action.payload?.subscriptionData?.subscriptionStatus) {
					const status =
						action.payload?.subscriptionData?.subscriptionStatus
					if (
						status === 'idle' ||
						status === 'trial' ||
						status === 'active' ||
						status === 'expired'
					) {
						state.subscriptionStatus = status
					}
					if (status === 'expired') {
						state.trialExpiredModalOpenStatus = true
					}
				}
			}
		)
		builder.addMatcher(
			userInfoApi.endpoints.fetchUserInfo.matchFulfilled,
			(state, action) => {
				state.userInformation = action.payload
			}
		)
		builder.addMatcher(
			quoteApi.endpoints.fetchCompanyQuoteTemplatesByTenantId
				.matchFulfilled,
			(state, action) => {
				if (action.payload?.companyQuoteTemplateData) {
					state.companyQuoteTemplateList =
						action.payload.companyQuoteTemplateData
				}
			}
		)
		builder.addMatcher(
			quoteApi.endpoints.fetchBasicCompanyQuoteTemplates.matchFulfilled,
			(state, action) => {
				if (action.payload?.companyQuoteTemplateData) {
					state.companyQuoteTemplateList =
						action.payload?.companyQuoteTemplateData
				}
			}
		)
		builder.addMatcher(
			userInfoApi.endpoints.fetchUserInfoByTenantId.matchFulfilled,
			(state, action) => {
				console.log('action.payload: ', action.payload)
				state.usersOfCompany = action.payload
			}
		)
	},
})

export const {
	setIsMenuOpen,
	setCommentSideBarOpen,
	setSortBy,
	setIsAccountSettingsOpen,
	setIsChangePasswordModalOpen,
	setIsUserProfileModalOpen,
	setIsImageUploadOpen,
	setIsLogoutOpen,
	setCompanyLogoUrl,
	setCompanyInfo,
	setProjectSelectDialogOpenStatus,
	setTAndCFormStatus,
	setIsUserMenuOpen,
	setIsChooseCompanyMenuOpen,
	setIsMenuDrawerOpen,
	setIsMenuDrawerBudgetingOpen,
	setIsMenuDrawerExpensesOpen,
	setIsMenuDrawerRevenuesOpen,
	setAdminTour,
	setIsAdminWalkthroughClosed,
	setTrialExpiredModalStatus,
	setChooseSubscriptionModalStatus,
	setAddNewUserModalStatus,
	appendCompanyName,
	setSubscriptionStatus,
} = settingsSlice.actions

export default settingsSlice.reducer
